// ./src/utils/api/RsvpGroupService.ts
//
// Named RSVP-together groups for an event ("Mr & Mrs Smith", "The Dallens").
// Members are event_guest rows sharing rsvpGroupId; the head is the guest with
// isGroupPrimary. Mirrors the eventsmagic-api RsvpGroup SDL (single-row CRUD).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const FIELDS = `
  id
  eventId
  accountId
  name
  personGroupId
  status
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query RsvpGroupList($filter: RsvpGroupFilter, $params: PaginationAndSorting) {
    rsvpGroupList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation RsvpGroupCreate($params: RsvpGroupInput) {
    rsvpGroupCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation RsvpGroupUpdate($where: RsvpGroupWhereInput, $params: RsvpGroupInput) {
    rsvpGroupUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RsvpGroupRemove($where: RsvpGroupWhereInput) {
    rsvpGroupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface RsvpGroup {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  name?: string | null;
  /** The address-book person_group (household) this group was derived from, if any. */
  personGroupId?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface RsvpGroupInput {
  eventId?: string | null;
  name?: string | null;
  personGroupId?: string | null;
}

export class RsvpGroupService {
  static async list(eventId: string): Promise<OpResult> {
    // NB: RsvpGroupFilter has no `status` field — passing one errors the query.
    return ApiService.executeQuery(LIST_QUERY, { filter: { eventId: [eventId] } });
  }

  /** The event's groups that were derived from the given address-book person groups (households) —
   *  used to REUSE an existing household group when adding more of its members later. */
  static async listForPersonGroups(eventId: string, personGroupIds: string[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], personGroupId: personGroupIds },
    });
  }

  /**
   * These groups of the event, by id — the names for one page of guests (the wizard's guest list, #62 P6c).
   * ⚠ The page size is the id count: an unpaged read stops at the server's default page, and the names past it would
   * silently go missing.
   */
  static async listByIds(eventId: string, ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], id: ids },
      params: { pagination: { page: 1, pageSize: Math.max(ids.length, 1) } },
    });
  }

  static async create(params: RsvpGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: RsvpGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
